import {Button} from '@ezcater/tapas';
import useTranslation from 'next-translate/useTranslation';
import {twMerge} from 'tailwind-merge';

import {DietaryOptionEnum} from '@/graphql/types';
import {useEventBarV2} from '../EventBarV2Provider';
import {useEventBarV2Tracking} from '../hooks/useEventBarV2Tracking';

type DietaryFiltersProps = {
  idSuffix: string;
};

const DIETARY_OPTIONS = [
  DietaryOptionEnum.Vegetarian,
  DietaryOptionEnum.Vegan,
  DietaryOptionEnum.GlutenFree,
  DietaryOptionEnum.Halal,
  DietaryOptionEnum.Kosher,
];

const DietaryFilters: React.FC<DietaryFiltersProps> = ({idSuffix}) => {
  const {t} = useTranslation('app-bar');
  const {dietaryOptions, setDietaryOptions, hasUserEditedDietaryRef} = useEventBarV2();
  const {trackDietary} = useEventBarV2Tracking();

  const labelId = `dietary-filters-label-${idSuffix}`;
  const helperId = `dietary-filters-helper-${idSuffix}`;

  const toggleOption = (option: DietaryOptionEnum) => {
    const next = dietaryOptions.includes(option)
      ? dietaryOptions.filter(value => value !== option)
      : [...dietaryOptions, option];
    // Mark the bar as diverged from the committed filters so the filter-sync doesn't stomp this
    // toggle before a search commits it.
    hasUserEditedDietaryRef.current = true;
    setDietaryOptions(next);
    trackDietary(next);
  };

  return (
    <div
      aria-describedby={helperId}
      aria-labelledby={labelId}
      className="flex w-full flex-col"
      role="group"
    >
      <span className="text-sm font-semibold text-peppercorn-800" id={labelId}>
        {t('eventBarV2.who.dietaryFilters.label')}
      </span>
      <div className="flex flex-wrap gap-2 pt-2">
        {DIETARY_OPTIONS.map(option => {
          const isSelected = dietaryOptions.includes(option);
          return (
            <Button
              aria-pressed={isSelected}
              className={twMerge(
                'gap-x-1 whitespace-nowrap rounded-full border-peppercorn-200 font-medium hover:border-transparent hover:bg-green-50',
                isSelected && 'border-transparent bg-green-50 ring-2 ring-green-300',
              )}
              data-testid={`who-dietary-chip-${option.toLowerCase().replace(/_/g, '-')}`}
              id={`dietary-filter-chip-${option}-${idSuffix}`}
              key={option}
              onClick={() => toggleOption(option)}
              size="small"
              type="button"
              variant="outlined"
            >
              {t(`eventBarV2.who.dietaryFilters.${option}`)}
            </Button>
          );
        })}
      </div>
    </div>
  );
};

export default DietaryFilters;
